import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import { useAdmin } from '@/hooks/useAdmin'
import { track } from '@/lib/analytics'

const navItems = [
  { emoji: '📋', label: 'Habits', href: '/' },
  { emoji: '🏆', label: 'Challenges', href: '/challenges' },
  { emoji: '👥', label: 'Community', href: '/community' },
  { emoji: '🎯', label: 'Focus', href: '/pomodoro' },
  { emoji: '⚙️', label: 'Settings', href: '/settings' },
]

export function Sidebar() {
  const location = useLocation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const { isAdmin: showAdminLink } = useAdmin()

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  return (
    <motion.aside
      initial={false}
      animate={{ width: isExpanded ? 200 : 64 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      className="fixed left-0 top-0 h-full z-50 hidden lg:flex flex-col bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 overflow-hidden pt-[var(--native-safe-area-inset-top,env(safe-area-inset-top,0px))]"
    >
      {/* Logo */}
      <Link to="/" className="p-4 flex items-center gap-3 h-16 border-b border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer">
        <div className="w-8 h-8 rounded-lg bg-slate-900 dark:bg-white flex items-center justify-center shrink-0">
          <FluentEmoji emoji="🧭" size={20} animated />
        </div>
        <AnimatePresence>
          {isExpanded && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="font-semibold text-slate-900 dark:text-white whitespace-nowrap"
            >
              Miac
            </motion.span>
          )}
        </AnimatePresence>
      </Link>

      {/* Nav Items */}
      <nav className="flex-1 py-4 px-2 space-y-1">
        {navItems.map((item) => {
          const active = isActive(item.href)
          const isHovered = hoveredItem === item.href
          
          return (
            <Link
              key={item.label}
              to={item.href}
              onClick={() => track('sidebar_nav_click', { target: item.href, label: item.label }, 'navigation')}
              onMouseEnter={() => setHoveredItem(item.href)}
              onMouseLeave={() => setHoveredItem(null)}
              className={cn(
                "flex items-center gap-3 px-3 h-12 rounded-xl transition-colors",
                active
                  ? "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white"
                  : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-700 dark:hover:text-slate-200"
              )}
            >
              <span className="w-6 h-6 flex items-center justify-center shrink-0">
                <FluentEmoji emoji={item.emoji} size={24} animated={active || isHovered} />
              </span>
              <AnimatePresence>
                {isExpanded && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="font-medium whitespace-nowrap"
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>
          )
        })}
      </nav>

      {/* Admin Link - Only for admin user */}
      {showAdminLink && (
        <div className="border-t border-slate-200 dark:border-slate-800 py-4 px-2">
          <Link
            to="/admin"
            onMouseEnter={() => setHoveredItem('/admin')}
            onMouseLeave={() => setHoveredItem(null)}
            className={cn(
              "flex items-center gap-3 px-3 h-12 rounded-xl transition-colors",
              location.pathname.startsWith('/admin')
                ? "bg-violet-100 dark:bg-violet-900/30 text-violet-900 dark:text-violet-300"
                : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-700 dark:hover:text-slate-200"
            )}
          >
            <span className="w-6 h-6 flex items-center justify-center shrink-0">
              <FluentEmoji emoji="🔐" size={24} animated={location.pathname.startsWith('/admin') || hoveredItem === '/admin'} />
            </span>
            <AnimatePresence>
              {isExpanded && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="font-medium whitespace-nowrap"
                >
                  Admin
                </motion.span>
              )}
            </AnimatePresence>
          </Link>
        </div>
      )}
    </motion.aside>
  )
}
